import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { apiRequest } from "@/lib/queryClient";
import { 
  Target, 
  Calendar, 
  BookOpen, 
  TrendingUp, 
  Users, 
  Shield, 
  Zap, 
  CheckCircle, 
  Settings, 
  Edit3, 
  Eye 
} from "lucide-react";
import type { PricingTemplate, FeatureComparison } from "@shared/schema";

interface PricingFormData {
  planName: string;
  price: number;
  currency: string;
  billingPeriod: string;
  description: string;
  features: string[];
  isPopular: boolean;
  buttonText: string;
  buttonColor: string;
}

export default function SampleLanding() {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPlan, setEditingPlan] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch current pricing data
  const { data: templates = [], isLoading } = useQuery<PricingTemplate[]>({
    queryKey: ["/api/landing-pages/pricing-templates"],
  });

  const { data: features = [] } = useQuery<FeatureComparison[]>({
    queryKey: ["/api/landing-pages/features"],
  });

  // Mock pricing data if none exists
  const mockPricingPlans = [
    {
      id: "starter",
      planName: "Starter",
      price: 0,
      currency: "USD",
      billingPeriod: "month",
      description: "Perfect for individuals getting started with goal tracking",
      features: ["5 active goals", "Basic habit tracking", "Personal calendar", "Simple analytics"],
      isPopular: false,
      buttonText: "Get Started Free",
      buttonColor: "#3b82f6"
    },
    {
      id: "pro",
      planName: "Pro",
      price: 15,
      currency: "USD", 
      billingPeriod: "month",
      description: "Advanced features for serious goal achievers",
      features: ["Unlimited goals", "Advanced habit tracking", "Team collaboration", "AI-powered insights", "Priority support"],
      isPopular: true,
      buttonText: "Start Pro Trial",
      buttonColor: "#7c3aed"
    },
    {
      id: "enterprise",
      planName: "Enterprise",
      price: 45,
      currency: "USD",
      billingPeriod: "month",
      description: "Complete solution for organizations and teams",
      features: ["Everything in Pro", "White-label branding", "Custom integrations", "Advanced analytics", "Dedicated support"],
      isPopular: false,
      buttonText: "Contact Sales",
      buttonColor: "#059669"
    }
  ];

  const currentPlans = templates.length > 0 ? templates : mockPricingPlans;

  // Pricing form
  const form = useForm<PricingFormData>({
    defaultValues: {
      planName: "",
      price: 0,
      currency: "USD",
      billingPeriod: "month",
      description: "",
      features: [],
      isPopular: false,
      buttonText: "Get Started",
      buttonColor: "#3b82f6"
    }
  });

  // Update pricing mutation
  const updatePricingMutation = useMutation({
    mutationFn: (data: PricingFormData) =>
      apiRequest("POST", "/api/landing-pages/pricing-templates", {
        ...data,
        templateName: data.planName.toLowerCase(),
        isActive: true
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/landing-pages/pricing-templates"] });
      setEditingPlan(null);
    },
    onError: (error: any) => {
    }
  });

  const handleEditPlan = (plan: any) => {
    form.reset({
      planName: plan.planName,
      price: plan.price,
      currency: plan.currency,
      billingPeriod: plan.billingPeriod,
      description: plan.description,
      features: Array.isArray(plan.features) ? plan.features : [],
      isPopular: plan.isPopular,
      buttonText: plan.buttonText,
      buttonColor: plan.buttonColor
    });
    setEditingPlan(plan.id);
  };

  const onSubmit = (data: PricingFormData) => {
    // Update the current plan in the mock data or real data
    updatePricingMutation.mutate(data);
  };

  // Tenant Owner Login mutation
  const tenantOwnerLoginMutation = useMutation({
    mutationFn: () =>
      apiRequest("POST", "/api/dev/login", { role: "owner" }),
    onSuccess: () => {
      // Redirect to dashboard after successful login
      window.location.href = "/";
    },
    onError: (error: any) => {
    }
  });

  const handleTenantOwnerLogin = () => {
    tenantOwnerLoginMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      {/* Header */}
      <header className="border-b bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg">
                <Target className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  GoalVia
                </span>
                <Badge variant="secondary" className="ml-2 text-xs">Sample Landing</Badge>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant={isEditMode ? "default" : "outline"}
                size="sm"
                onClick={() => setIsEditMode(!isEditMode)}
                data-testid="button-toggle-edit-mode"
              >
                {isEditMode ? <Eye className="h-4 w-4 mr-2" /> : <Edit3 className="h-4 w-4 mr-2" />}
                {isEditMode ? "Preview Mode" : "Edit Mode"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleTenantOwnerLogin}
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="button-tenant-owner-login"
              >
                <Users className="h-4 w-4 mr-2" />
                Tenant Owner Login
              </Button>
              <Button 
                onClick={() => window.location.href = "/api/login"}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                data-testid="button-get-started"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Badge variant="secondary" className="mb-4 px-3 py-1">
              🚀 The Future of Productivity
            </Badge>
            <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight">
              Transform Your{" "}
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent">
                Goals Into Reality
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              The comprehensive platform that combines goal tracking, habit formation, 
              journaling, and calendar management to help teams achieve more together.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg px-8 py-3"
                data-testid="button-start-free-trial"
              >
                Start Free Trial
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="text-lg px-8 py-3"
                data-testid="button-watch-demo"
              >
                Watch Demo
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Powerful features designed to help you set, track, and achieve your most important goals.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Target,
                title: "Smart Goal Tracking",
                description: "Set SMART goals with deadlines, milestones, and automated progress tracking."
              },
              {
                icon: Calendar,
                title: "Integrated Calendar",
                description: "Seamlessly schedule goal-related tasks and track important deadlines."
              },
              {
                icon: BookOpen,
                title: "Progress Journaling", 
                description: "Reflect on your journey with guided journaling and insights."
              },
              {
                icon: TrendingUp,
                title: "Analytics Dashboard",
                description: "Visualize your progress with detailed charts and performance metrics."
              }
            ].map((feature, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-shadow duration-300">
                <CardHeader className="text-center pb-4">
                  <div className="mx-auto w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 dark:text-gray-300 text-center">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Choose the plan that fits your needs. All plans include our core features.
            </p>
            {isEditMode && (
              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-blue-800 dark:text-blue-200 font-medium">
                  🎯 Edit Mode Active - Click on any pricing card to modify it
                </p>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {currentPlans.map((plan: any, index: number) => (
              <Card 
                key={plan.id} 
                className={`relative border-2 transition-all duration-300 hover:shadow-xl ${
                  plan.isPopular 
                    ? "border-purple-500 shadow-lg scale-105" 
                    : "border-gray-200 dark:border-gray-700 hover:border-blue-300"
                } ${isEditMode ? "cursor-pointer hover:ring-2 hover:ring-blue-400" : ""}`}
                onClick={isEditMode ? () => handleEditPlan(plan) : undefined}
                data-testid={`card-pricing-${plan.planName.toLowerCase()}`}
              >
                {plan.isPopular && (
                  <Badge 
                    className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-1"
                    data-testid="badge-most-popular"
                  >
                    Most Popular
                  </Badge>
                )}

                {isEditMode && (
                  <div className="absolute top-4 right-4">
                    <Settings className="h-5 w-5 text-blue-600" />
                  </div>
                )}

                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl font-bold">{plan.planName}</CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">
                      ${plan.price}
                    </span>
                    <span className="text-gray-600 dark:text-gray-300">/{plan.billingPeriod}</span>
                  </div>
                  <CardDescription className="mt-4 text-base">
                    {plan.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  <Button 
                    className="w-full text-lg py-3"
                    style={{ backgroundColor: plan.buttonColor }}
                    data-testid={`button-select-${plan.planName.toLowerCase()}`}
                  >
                    {plan.buttonText}
                  </Button>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 dark:text-white">What's included:</h4>
                    <ul className="space-y-2">
                      {plan.features.map((feature: string, featureIndex: number) => (
                        <li key={featureIndex} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600 dark:text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Interactive Pricing Editor */}
          {isEditMode && (
            <Dialog open={!!editingPlan} onOpenChange={() => setEditingPlan(null)}>
              <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Edit Pricing Plan</DialogTitle>
                  <DialogDescription>
                    Modify the pricing plan details and see the changes reflected immediately.
                  </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="planName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Plan Name</FormLabel>
                            <FormControl>
                              <Input 
                                {...field}
                                placeholder="e.g., Pro, Enterprise"
                                data-testid="input-plan-name"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="price"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Price</FormLabel>
                            <FormControl>
                              <Input 
                                type="number"
                                {...field}
                                onChange={(e) => field.onChange(Number(e.target.value))}
                                placeholder="29"
                                data-testid="input-price"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Description</FormLabel>
                          <FormControl>
                            <Textarea 
                              {...field}
                              placeholder="Brief description of what this plan includes..."
                              rows={3}
                              data-testid="textarea-plan-description"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="buttonText"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Button Text</FormLabel>
                          <FormControl>
                            <Input 
                              {...field}
                              placeholder="Get Started, Contact Sales, etc."
                              data-testid="input-button-text"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="buttonColor"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Button Color</FormLabel>
                          <FormControl>
                            <Input 
                              type="color"
                              {...field}
                              className="h-12 w-full"
                              data-testid="input-button-color"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="flex items-center space-x-2">
                      <FormField
                        control={form.control}
                        name="isPopular"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-2">
                            <FormControl>
                              <Switch
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                data-testid="switch-is-popular"
                              />
                            </FormControl>
                            <FormLabel className="text-base font-normal">
                              Mark as "Most Popular"
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                    </div>

                    <div className="flex gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEditingPlan(null)}
                        data-testid="button-cancel-edit"
                      >
                        Cancel
                      </Button>
                      <Button 
                        type="submit"
                        disabled={updatePricingMutation.isPending}
                        data-testid="button-save-pricing"
                      >
                        {updatePricingMutation.isPending ? "Saving..." : "Save Changes"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </section>

      {/* Features Comparison Section */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Compare Plans & Features
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              See what's included in each plan to find the perfect fit for your needs.
            </p>
          </div>

          <div className="w-full max-w-full md:max-w-5xl mx-auto px-4 md:px-0">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="font-semibold text-lg">Features</div>
              {currentPlans.map((plan: any) => (
                <div key={plan.id} className="text-center">
                  <h3 className="font-bold text-lg">{plan.planName}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    ${plan.price}/{plan.billingPeriod}
                  </p>
                </div>
              ))}
            </div>

            <div className="space-y-4">
              {[
                { label: "Active Goals", starter: "5", pro: "Unlimited", enterprise: "Unlimited" },
                { label: "Habit Tracking", starter: "Basic", pro: "Advanced", enterprise: "Advanced + Analytics" },
                { label: "Team Collaboration", starter: "✗", pro: "✓", enterprise: "✓ + Admin Controls" },
                { label: "AI Insights", starter: "✗", pro: "✓", enterprise: "✓ + Custom Models" },
                { label: "Data Export", starter: "✗", pro: "✓", enterprise: "✓ + API Access" },
                { label: "Priority Support", starter: "✗", pro: "✓", enterprise: "✓ + Dedicated Manager" }
              ].map((row, index) => (
                <div key={index} className="grid grid-cols-2 md:grid-cols-4 gap-4 py-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="font-medium">{row.label}</div>
                  <div className="text-center text-gray-600 dark:text-gray-300">{row.starter}</div>
                  <div className="text-center text-gray-600 dark:text-gray-300">{row.pro}</div>
                  <div className="text-center text-gray-600 dark:text-gray-300">{row.enterprise}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Productivity?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of users who have already achieved their goals with GoalVia.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              variant="secondary"
              className="text-lg px-8 py-3 bg-white text-blue-600 hover:bg-gray-100"
              data-testid="button-start-free"
            >
              Start Free Today
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="text-lg px-8 py-3 border-white text-white hover:bg-white/10"
              data-testid="button-contact-sales"
            >
              Contact Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Target className="h-6 w-6" />
                <span className="text-xl font-bold">GoalVia</span>
              </div>
              <p className="text-gray-400">
                The comprehensive productivity platform for achieving your goals.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Features</a></li>
                <li><a href="#" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">API</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">About</a></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white">Help Center</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
                <li><a href="#" className="hover:text-white">Status</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 GoalVia. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}